import React, { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  TextField,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import SelectionService from "../../../services/SelectionService";
import StepperNav from "../StepperNav";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Summary() {
  const { t } = useTranslation();
  const trajectoryModeOptions = [
    { id: "car", name: t("trajectories.wizard.summary.trajectoryModes.car") },
    { id: "fleet", name: t("trajectories.wizard.summary.trajectoryModes.fleet") },
    { id: "combined", name: t("trajectories.wizard.summary.trajectoryModes.combined") },
  ];
  const [dates, setDates] = useState([]);
  const [times, setTimes] = useState([]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [selectedTimeIndex, setSelectedTimeIndex] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [name, setName] = useState("");
  const [trajectoryMode, setTrajectoryMode] = useState("combined");
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;
  useEffect(() => {
    setDates(SelectionService.getDates());
    setTimes(SelectionService.getTimes());
    setSelectedDateIndex(SelectionService.getSelectedDate());
    setSelectedTimeIndex(SelectionService.getSelectedTime());
    setSelectedLink(SelectionService.getSelectedLink());
    setTrajectoryMode(SelectionService.getTrajectoryMode() || "combined");
    console.log(SelectionService.getTimes());
    console.log(SelectionService.getDates());
  }, []);

  const handleProceedToNextStep = () => {
    navigate("/trajectories/result"); // Proceed to the next step
  };

  const handleProceedToPreviousStep = () => {
    navigate("/trajectories/link", { state: { userId: userId } }); // Go back to the previous step
  };

  return (
    <DashboardLayout>
      <Grid
        container
        direction="row"
        sx={{ alignItems: "stretch", height: "87vh" }}
      >
        <Box
          sx={{
            width: "30%",
            height: "87vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container direction="column" sx={{ flex: 1 }}>
            <Grid item xs={8} sx={{ flexGrow: 1 }}>
              <Card sx={{ mb: 3 }}>
                <CardHeader title={t("trajectories.wizard.summary.cards.title")} />
                <CardContent>
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    multiline
                    maxRows={2}
                    fullWidth
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      SelectionService.setReportName(e.target.value);
                    }}
                  />
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }}>
                <CardHeader title={t("trajectories.wizard.summary.datasource")} />
                <CardContent>
                  <Autocomplete
                    value={
                      trajectoryModeOptions.find(
                        (option) => option.id === trajectoryMode
                      ) || null
                    }
                    options={trajectoryModeOptions}
                    getOptionLabel={(option) => option.name}
                    onChange={(e, selectedOption) => {
                      const nextValue = selectedOption?.id || "combined";
                      setTrajectoryMode(nextValue);
                      SelectionService.setTrajectoryMode(nextValue);
                    }}
                    renderInput={(params) => (
                      <MDInput {...params} variant="standard" />
                    )}
                  />
                </CardContent>
              </Card>

              {/* Dates Card */}
              {dates.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader
                    title={t("trajectories.wizard.summary.cards.dates")}
                  />
                  <CardContent>
                    <Typography variant="body1" color="HighlightText">
                      {dates[0].title}
                    </Typography>
                    <Typography variant="body2" color="HighlightText">
                      {dayjs(
                        dates[SelectionService.getSelectedDate()].startDate
                      ).format("DD/MM/YYYY")}{" "}
                      -{" "}
                      {dayjs(
                        dates[SelectionService.getSelectedDate()].endDate
                      ).format("DD/MM/YYYY")}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Times Card */}
              {times.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader
                    title={t("trajectories.wizard.summary.cards.times")}
                  />
                  <CardContent>
                    <Typography variant="body1" color="HighlightText">
                      {times[0].allDay
                        ? t("trajectories.wizard.summary.values.allDay")
                        : `${times[SelectionService.getSelectedTime()].from[0].slice(11, 16)} - ${times[SelectionService.getSelectedTime()].to[0].slice(11, 16)}`}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Selected Link Card */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={t("trajectories.wizard.summary.cards.selectedLink")}
                />
                <CardContent>
                  {selectedLink ? (
                    <Box>
                      <Typography color="HighlightText">
                        {t("trajectories.wizard.summary.labels.wayId")}{" "}
                        {selectedLink.properties.wayId}{" "}
                      </Typography>
                      <Typography color="HighlightText">
                        {t("trajectories.wizard.summary.labels.name")}{" "}
                        {selectedLink.properties.name}{" "}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="HighlightText">
                      {t("trajectories.wizard.summary.values.noLink")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {/* Navigation Buttons */}
            <Grid
              item
              xs="auto"
              sx={{
                mt: "auto", // Pushes the grid to the bottom of the container
                textAlign: "right", // Aligns buttons to the right
              }}
            >
              <Grid container justifyContent="flex-end" spacing={2}>
                <Grid item>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="medium"
                    onClick={handleProceedToPreviousStep}
                  >
                    {t("trajectories.wizard.summary.actions.back")}
                  </MDButton>
                </Grid>
                <Grid item>
                  <MDButton
                    variant="contained"
                    color="info"
                    size="medium"
                    onClick={handleProceedToNextStep}
                  >
                    {t("trajectories.wizard.summary.actions.result")}
                  </MDButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flex: 1 }}>
          <StepperNav />
        </Box>
      </Grid>
    </DashboardLayout>
  );
}

export default Summary;
